import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './PriceRangeSlider.module.css';

const BUCKET_COUNT = 30;

function valueToPercent(value: number, min: number, max: number) {
  return ((value - min) / (max - min)) * 100;
}

function formatEuro(value: number) {
  return `${value} €`;
}

interface PriceRangeSliderProps {
  prices: number[];
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

export function PriceRangeSlider({ prices, min, max, step, value, onChange }: PriceRangeSliderProps) {
  const [minVal, maxVal] = value;
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const histogram = useMemo(() => {
    const buckets = new Array(BUCKET_COUNT).fill(0);
    const range = max - min;
    if (range <= 0) return buckets;
    for (const p of prices) {
      const idx = Math.min(Math.floor(((p - min) / range) * BUCKET_COUNT), BUCKET_COUNT - 1);
      buckets[idx]++;
    }
    const peak = Math.max(...buckets, 1);
    return buckets.map(v => v / peak);
  }, [prices, min, max]);

  const resolveValue = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return null;
      const rect = sliderRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      return Math.round((min + (pct / 100) * (max - min)) / step) * step;
    },
    [min, max, step]
  );

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const v = resolveValue(clientX);
      if (v === null) return;
      if (dragging === 'min') onChange([Math.min(v, maxVal - step), maxVal]);
      else onChange([minVal, Math.max(v, minVal + step)]);
    };

    const onUp = () => setDragging(null);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    };
  }, [dragging, minVal, maxVal, step, resolveValue, onChange]);

  const minPct = valueToPercent(minVal, min, max);
  const maxPct = valueToPercent(maxVal, min, max);

  return (
    <div className={styles.wrapper}>
      <div className={styles.histogram} ref={sliderRef}>
        {histogram.map((h, i) => {
          const barPct = (i / (BUCKET_COUNT - 1)) * 100;
          const inRange = barPct >= minPct && barPct <= maxPct;
          return (
            <div
              key={i}
              className={`${styles.bar} ${inRange ? styles.barActive : styles.barInactive}`}
              style={{ height: `${Math.max(h * 100, 4)}%` }}
            />
          );
        })}
      </div>

      <div className={styles.track}>
        <button
          className={styles.thumb}
          style={{ left: `${minPct}%` }}
          onMouseDown={() => setDragging('min')}
          onTouchStart={() => setDragging('min')}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={maxVal - step}
          aria-valuenow={minVal}
          aria-label="Prix minimum"
        />
        <button
          className={styles.thumb}
          style={{ left: `${maxPct}%` }}
          onMouseDown={() => setDragging('max')}
          onTouchStart={() => setDragging('max')}
          role="slider"
          aria-valuemin={minVal + step}
          aria-valuemax={max}
          aria-valuenow={maxVal}
          aria-label="Prix maximum"
        />
      </div>

      <div className={styles.values}>
        <span className={styles.value}>{formatEuro(minVal)}</span>
        <span className={styles.value}>{formatEuro(maxVal)}</span>
      </div>
    </div>
  );
}
